import { useState } from "react";
import { useConnections } from "../lib/realdata";
import type { LatentTheme, PremiumLiftRow, StretchRow, Revelation } from "../lib/realdata";

export default function Connections() {
  const data = useConnections();

  if (!data) {
    return <div className="loading-card"><div className="lab muted">Loading connections…</div></div>;
  }

  return (
    <div className="stagger">
      <section className="page-hero connections-hero">
        <div className="kicker">v3 · AI cross-category synthesis</div>
        <h1>What Pulse found across <em className="warn">MG</em> and <em className="warn">Fans</em> that nobody read separately would have caught</h1>
        <p className="muted">
          Three analyses that only become possible when both studies are in one analytical brain at once. Statistical clustering reveals the same need-states underneath very different categories. Cross-study segment slicing reveals a single premium-portfolio persona. And brand-positioning, normalised to remove survey-methodology noise, reveals where Crompton fights as a peer vs trails as a follower.
        </p>
      </section>

      <RevelationStrip revelations={data.revelations} />

      <LatentThemesSection themes={data.latent_themes} mgN={data.meta.mg_n} fansN={data.meta.fans_n} />

      <PremiumPersonaSection
        summary={data.premium_persona.summary}
        rows={data.premium_persona.rows}
        mgPremN={data.meta.mg_premium_n}
        fansPremN={data.meta.fans_premium_n}
      />

      <BrandStretchSection
        summary={data.brand_stretch.summary}
        methodNote={data.brand_stretch.method_note}
        rows={data.brand_stretch.rows}
        consistent={data.brand_stretch.crompton_consistent_count}
        stretch={data.brand_stretch.crompton_stretch_count}
      />
    </div>
  );
}

// ----------------------------------------------------------------------
// AI Revelations strip — three card carousel
// ----------------------------------------------------------------------
function RevelationStrip({ revelations }: { revelations: Revelation[] }) {
  return (
    <section className="rev-strip">
      <div className="rev-strip-head">
        <div className="rev-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
            <path d="M9 18h6"/><path d="M10 22h4"/>
            <path d="M12 2a7 7 0 0 0-4 12.7c.7.6 1 1.4 1 2.3v1h6v-1c0-.9.3-1.7 1-2.3A7 7 0 0 0 12 2Z"/>
          </svg>
        </div>
        <div>
          <h2>AI revelations</h2>
          <div className="muted">Three findings that emerge only when both studies are analysed together. Each has the headline, the statistical evidence, why it would be missed by reading the studies in isolation, and a recommended action.</div>
        </div>
      </div>
      <div className="rev-grid">
        {revelations.map((r, i) => <RevelationCard key={r.id} r={r} idx={i + 1} />)}
      </div>
    </section>
  );
}

function RevelationCard({ r, idx }: { r: Revelation; idx: number }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={"rev-card" + (open ? " open" : "")}>
      <div className="rev-num">#{idx}</div>
      <h3>{r.headline}</h3>
      <p className="rev-body">{r.body}</p>
      {open && (
        <div className="rev-detail">
          <div className="rev-detail-row">
            <div className="rev-key">Statistical evidence</div>
            <div className="rev-val">{r.evidence}</div>
          </div>
          <div className="rev-detail-row">
            <div className="rev-key">Why it stays hidden</div>
            <div className="rev-val">{r.why_hidden}</div>
          </div>
          <div className="rev-detail-row">
            <div className="rev-key">Recommended action</div>
            <div className="rev-val rev-action">{r.action}</div>
          </div>
        </div>
      )}
      <button className="rev-toggle" onClick={() => setOpen(!open)}>
        {open ? "Hide details" : "See evidence + action"}
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? "rotate(180deg)" : "none" }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
    </div>
  );
}

// ----------------------------------------------------------------------
// Latent need-states section
// ----------------------------------------------------------------------
function LatentThemesSection({ themes, mgN, fansN }: { themes: LatentTheme[]; mgN: number; fansN: number }) {
  return (
    <section className="conn-section">
      <div className="conn-head">
        <div className="conn-num">01</div>
        <div>
          <h2>The same psychological need-states underpin both categories</h2>
          <p className="muted">
            Pulse computes pairwise Pearson correlations between attribute importance ratings within each study (MG n={mgN.toLocaleString()}, Fans n={fansN.toLocaleString()}). Attributes whose ratings consistently move together belong to the same underlying need. The discovery: <strong>the same five need-states emerge in both studies, with comparable correlation strength</strong>. Consumers organise their thinking around the same psychology even across very different products.
          </p>
        </div>
      </div>

      <div className="theme-cards">
        {themes.map((t) => <ThemeCard key={t.id} t={t} />)}
      </div>
    </section>
  );
}

function ThemeCard({ t }: { t: LatentTheme }) {
  return (
    <div className={"theme-pair-card" + (t.validated ? " validated" : " weak")}>
      <div className="tp-head">
        <h3>{t.label}</h3>
        {t.validated && <span className="pill teal">Validated in both studies</span>}
      </div>
      <p className="tp-narr muted">{t.narrative}</p>
      <div className="tp-cols">
        <div className="tp-col">
          <div className="tp-col-head">
            <span className="tp-cat-tag teal">MG</span>
            <span className="tp-r">avg r = {t.mg_avg_corr.toFixed(2)}</span>
          </div>
          <ul className="tp-members">
            {t.mg_members.map((m, i) => <li key={i}>{m.label}</li>)}
          </ul>
          {t.mg_evidence.length > 0 && (
            <div className="tp-ev">
              <div className="tp-ev-head">Strongest pair:</div>
              <div className="tp-ev-row">r = {t.mg_evidence[0].r.toFixed(2)} · {t.mg_evidence[0].a} ↔ {t.mg_evidence[0].b}</div>
            </div>
          )}
        </div>
        <div className="tp-col">
          <div className="tp-col-head">
            <span className="tp-cat-tag amber">Fans</span>
            <span className="tp-r">avg r = {t.fans_avg_corr.toFixed(2)}</span>
          </div>
          <ul className="tp-members">
            {t.fans_members.map((m, i) => <li key={i}>{m.label}</li>)}
          </ul>
          {t.fans_evidence.length > 0 && (
            <div className="tp-ev">
              <div className="tp-ev-head">Strongest pair:</div>
              <div className="tp-ev-row">r = {t.fans_evidence[0].r.toFixed(2)} · {t.fans_evidence[0].a} ↔ {t.fans_evidence[0].b}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Premium portfolio persona section
// ----------------------------------------------------------------------
function PremiumPersonaSection({ summary, rows, mgPremN, fansPremN }: {
  summary: string; rows: PremiumLiftRow[]; mgPremN: number; fansPremN: number;
}) {
  // Sort by joint_lift, show top + flag attributes that lift in BOTH categories
  const topRows = rows.slice().sort((a, b) => b.joint_lift - a.joint_lift).slice(0, 12);
  const liftRange = Math.max(0.5, ...rows.flatMap(r => [Math.abs(r.mg_lift), Math.abs(r.fans_lift)]));

  return (
    <section className="conn-section">
      <div className="conn-head">
        <div className="conn-num">02</div>
        <div>
          <h2>A single premium-portfolio buyer persona spans both categories</h2>
          <p className="muted">
            Splitting both studies by the affluent flag (D9a; MG affluent n={mgPremN}, Fans affluent n={fansPremN}) and computing per-attribute importance lift reveals where premium buyers diverge from the mass sample. The discovery: the same brand-level attributes lift in both categories, defining a portfolio segment - one campaign for affluent metros lifts brand equity for MG and Fans together.
          </p>
        </div>
      </div>

      <p className="conn-summary">{summary}</p>

      <div className="lift-table-card">
        <table className="lift-table">
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Attribute</th>
              <th>Layer</th>
              <th>MG full</th>
              <th>MG premium</th>
              <th>MG lift</th>
              <th></th>
              <th>Fans full</th>
              <th>Fans premium</th>
              <th>Fans lift</th>
              <th></th>
              <th>Joint</th>
            </tr>
          </thead>
          <tbody>
            {topRows.map((r) => (
              <tr key={r.label} className={r.mg_lift > 0.05 && r.fans_lift > 0.05 ? "row-both-lift" : ""}>
                <td style={{ textAlign: "left" }}>{r.label}</td>
                <td><span className={"pill " + (r.layer === "brand" ? "teal" : "neutral")}>{r.layer}</span></td>
                <td>{r.mg_full.toFixed(2)}</td>
                <td>{r.mg_premium.toFixed(2)}</td>
                <td className={r.mg_lift > 0 ? "pos" : "neg"}>{(r.mg_lift > 0 ? "+" : "") + r.mg_lift.toFixed(2)}</td>
                <td><LiftBar v={r.mg_lift} max={liftRange} /></td>
                <td>{r.fans_full.toFixed(2)}</td>
                <td>{r.fans_premium.toFixed(2)}</td>
                <td className={r.fans_lift > 0 ? "pos" : "neg"}>{(r.fans_lift > 0 ? "+" : "") + r.fans_lift.toFixed(2)}</td>
                <td><LiftBar v={r.fans_lift} max={liftRange} /></td>
                <td className={"joint " + (r.joint_lift > 0 ? "pos" : "neg")}>{(r.joint_lift > 0 ? "+" : "") + r.joint_lift.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="muted small lift-foot">Highlighted rows show attributes that lift in <strong>both</strong> categories - portfolio-level premium signals.</div>
      </div>
    </section>
  );
}

function LiftBar({ v, max }: { v: number; max: number }) {
  const pct = Math.min(100, Math.abs(v) / max * 100);
  const color = v >= 0 ? "var(--ok)" : "var(--coral)";
  return (
    <div className="liftbar-cell">
      <div className="liftbar">
        {v >= 0 ? (
          <div className="lb-fill lb-pos" style={{ width: `${pct/2}%`, background: color }} />
        ) : (
          <div className="lb-fill lb-neg" style={{ width: `${pct/2}%`, background: color, marginRight: "auto" }} />
        )}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Brand stretch (Crompton indexed to Bajaj)
// ----------------------------------------------------------------------
function BrandStretchSection({ summary, methodNote, rows, consistent, stretch }: {
  summary: string; methodNote: string; rows: StretchRow[]; consistent: number; stretch: number;
}) {
  // Visualise: scatter of CG-index-MG (x) vs CG-index-Fans (y), with diagonal = consistent
  const W = 700, H = 480, P = 56;
  const valid = rows.filter(r => r.crompton_index_mg != null && r.crompton_index_fans != null);
  const lo = 0.5, hi = 1.4;
  const x = (v: number) => P + ((v - lo) / (hi - lo)) * (W - 2 * P);
  const y = (v: number) => H - P - ((v - lo) / (hi - lo)) * (H - 2 * P);

  return (
    <section className="conn-section">
      <div className="conn-head">
        <div className="conn-num">03</div>
        <div>
          <h2>Crompton's positioning vs Bajaj is structurally different in MG vs Fans</h2>
          <p className="muted">
            Raw imagery shares aren't comparable across studies (Fans respondents pick more brands per attribute than MG respondents). To isolate brand positioning from survey methodology, Pulse indexes Crompton's share to Bajaj's within each category - 1.00 = parity with the leader, less than 1.00 = trailing. The discovery: in MG, Crompton fights Bajaj at parity. In Fans, Crompton trails by ~22-25%. This is a Fans-specific positioning gap, not a portfolio-wide brand-stretch problem.
          </p>
        </div>
      </div>

      <p className="conn-summary">{summary}</p>

      <div className="stretch-grid">
        <div className="stretch-summary-cards">
          <div className="ss-card teal">
            <div className="ss-num">{consistent}</div>
            <div className="ss-lab">Consistent attributes</div>
            <div className="ss-sub muted">Crompton's positioning carries across categories</div>
          </div>
          <div className="ss-card coral">
            <div className="ss-num">{stretch}</div>
            <div className="ss-lab">Stretch attributes</div>
            <div className="ss-sub muted">Crompton perceived very differently in MG vs Fans</div>
          </div>
          <div className="ss-card neutral">
            <div className="ss-num">{rows.length - consistent - stretch}</div>
            <div className="ss-lab">Not surveyed</div>
            <div className="ss-sub muted">Statement not asked in MG A3 imagery battery</div>
          </div>
        </div>

        <div className="stretch-scatter-card">
          <h3>Crompton's index-to-Bajaj, by category</h3>
          <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxHeight: 520 }}>
            <rect x={P} y={P} width={W - 2 * P} height={H - 2 * P} fill="var(--card-2)" stroke="var(--line)" />

            {/* Diagonal = perfectly consistent positioning */}
            <line x1={x(lo)} y1={y(lo)} x2={x(hi)} y2={y(hi)} stroke="var(--ink-20)" strokeWidth="1.5" strokeDasharray="6 4" />
            <text x={x(1.3)} y={y(1.3) - 10} fontSize="10.5" fill="var(--ink-60)" textAnchor="end">Equal positioning across categories</text>

            {/* Parity-with-leader markers (1.0) */}
            <line x1={x(1)} y1={P} x2={x(1)} y2={H - P} stroke="var(--coral)" strokeWidth="1" strokeDasharray="3 3" opacity="0.5" />
            <line x1={P} y1={y(1)} x2={W - P} y2={y(1)} stroke="var(--coral)" strokeWidth="1" strokeDasharray="3 3" opacity="0.5" />
            <text x={x(1) + 4} y={P + 14} fontSize="10" fill="var(--coral-ink)" fontWeight="600">Parity with Bajaj</text>

            {/* Axes */}
            {[0.6, 0.8, 1.0, 1.2, 1.4].map((t) => (
              <g key={`xt${t}`}>
                <line x1={x(t)} y1={H - P} x2={x(t)} y2={H - P + 4} stroke="var(--ink-20)" />
                <text x={x(t)} y={H - P + 16} textAnchor="middle" fontSize="10" fill="var(--ink-60)">{t.toFixed(1)}</text>
              </g>
            ))}
            {[0.6, 0.8, 1.0, 1.2, 1.4].map((t) => (
              <g key={`yt${t}`}>
                <line x1={P - 4} y1={y(t)} x2={P} y2={y(t)} stroke="var(--ink-20)" />
                <text x={P - 8} y={y(t) + 3} textAnchor="end" fontSize="10" fill="var(--ink-60)">{t.toFixed(1)}</text>
              </g>
            ))}
            <text x={W / 2} y={H - 12} textAnchor="middle" fontSize="11" fill="var(--ink-60)">Crompton index in MG (vs Bajaj) →</text>
            <text x={14} y={H / 2} textAnchor="middle" fontSize="11" fill="var(--ink-60)" transform={`rotate(-90 14 ${H / 2})`}>Crompton index in Fans (vs Bajaj) →</text>

            {/* Dots */}
            {valid.map((r) => {
              const isStretch = r.stretch_class === "severe" || r.stretch_class === "stretch";
              return (
                <g key={r.label}>
                  <circle cx={x(r.crompton_index_mg!)} cy={y(r.crompton_index_fans!)}
                    r={isStretch ? 8 : 6}
                    fill={isStretch ? "var(--coral)" : "var(--teal)"}
                    fillOpacity={0.85} stroke="white" strokeWidth="1.5" />
                  <text x={x(r.crompton_index_mg!) + 11} y={y(r.crompton_index_fans!) + 4}
                    fontSize="10.5" fill={isStretch ? "var(--coral-ink)" : "var(--teal-ink)"}
                    fontWeight={isStretch ? 600 : 500}>
                    {r.label.length > 26 ? r.label.slice(0, 24) + "…" : r.label}
                  </text>
                </g>
              );
            })}
          </svg>
          <div className="muted small" style={{ marginTop: 8 }}>
            All attributes with both indices fall BELOW the diagonal - Crompton is consistently weaker vs Bajaj in Fans than in MG. Coral dots = stretch class.
          </div>
        </div>
      </div>

      <div className="lens-table-card">
        <h3>All 11 brand attributes - Crompton's positioning audit</h3>
        <p className="muted small" style={{ marginTop: -4, marginBottom: 12 }}>{methodNote}</p>
        <table className="cmp-table">
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Brand attribute</th>
              <th>CG MG</th>
              <th>BJ MG</th>
              <th>Index MG</th>
              <th>CG Fans</th>
              <th>BJ Fans</th>
              <th>Index Fans</th>
              <th>Gap</th>
              <th>Class</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const cls = r.stretch_class;
              const cellClr = cls === "severe" ? "var(--coral)" : cls === "stretch" ? "var(--amber)" : cls === "consistent" ? "var(--ok)" : "var(--ink-40)";
              return (
                <tr key={r.label}>
                  <td style={{ textAlign: "left" }}>{r.label}</td>
                  <td>{r.crompton_mg != null ? `${r.crompton_mg.toFixed(1)}%` : "-"}</td>
                  <td>{r.bajaj_mg != null ? `${r.bajaj_mg.toFixed(1)}%` : "-"}</td>
                  <td>{r.crompton_index_mg != null ? r.crompton_index_mg.toFixed(2) : "-"}</td>
                  <td>{r.crompton_fans != null ? `${r.crompton_fans.toFixed(1)}%` : "-"}</td>
                  <td>{r.bajaj_fans != null ? `${r.bajaj_fans.toFixed(1)}%` : "-"}</td>
                  <td>{r.crompton_index_fans != null ? r.crompton_index_fans.toFixed(2) : "-"}</td>
                  <td>{r.index_gap != null ? r.index_gap.toFixed(2) : "-"}</td>
                  <td><span className="pill" style={{ background: "transparent", color: cellClr, border: `1px solid ${cellClr}` }}>{cls}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
